import { Component } from '@theme/component';
const KEEP_FILTER_DRAWER_OPEN_STATE_KEY = 'keep-filter-drawer-open-state';

/**
 * Collection Filter Drawer Component
 * Manages the filter drawer for collection pages
 * Opens drawer from the right side
 */
export class CollectionFilterDrawerComponent extends Component {
  static ANIMATION_DURATION = 300;
  static DRAWER_SELECTOR = 'dialog-component:is(#filters-drawer, #collection-banner-filters-drawer)';

  connectedCallback() {
    super.connectedCallback();
    this.setupEventListeners();
    this.initializeDrawerAccordions();
    this.restoreDrawerState();
  }

  setupEventListeners() {
    // Handle filter & sort button clicks
    const buttons = document.querySelectorAll('[data-filter-drawer-toggle]');
    buttons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.openFilterDrawer();
      });
    });

    // Observe for dynamically added buttons
    this.observeDOM();
  }

  observeDOM() {
    const observer = new MutationObserver(() => {
      const newButtons = document.querySelectorAll('[data-filter-drawer-toggle]:not([data-initialized])');
      newButtons.forEach((button) => {
        button.setAttribute('data-initialized', 'true');
        button.addEventListener('click', (e) => {
          e.preventDefault();
          this.openFilterDrawer();
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  openFilterDrawer() {
    const filterDrawer = document.querySelector(CollectionFilterDrawerComponent.DRAWER_SELECTOR);
    if (filterDrawer && typeof filterDrawer.showDialog === 'function') {
      // Add right-side drawer class if not already present
      const dialog = filterDrawer.querySelector('dialog');
      if (dialog && !dialog.classList.contains('dialog-drawer--right')) {
        dialog.classList.add('dialog-drawer--right');
      }
      dialog?.classList.remove('filter-drawer--opened');
      this.initializeDrawerAccordions();
      filterDrawer.showDialog();
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          dialog?.classList.add('filter-drawer--opened');
        });
      });
    }
  }

  restoreDrawerState() {
    let savedState = null;
    try {
      savedState = window.sessionStorage.getItem(KEEP_FILTER_DRAWER_OPEN_STATE_KEY);
    } catch (_error) {
      return;
    }

    if (!savedState) return;

    let parsedState = null;
    try {
      parsedState = JSON.parse(savedState);
    } catch (_error) {
      window.sessionStorage.removeItem(KEEP_FILTER_DRAWER_OPEN_STATE_KEY);
      return;
    }

    const drawerId = parsedState?.drawerId;
    const keepSortingOpen = parsedState?.keepSortingOpen === true;
    if (!drawerId) {
      window.sessionStorage.removeItem(KEEP_FILTER_DRAWER_OPEN_STATE_KEY);
      return;
    }

    window.requestAnimationFrame(() => {
      const drawer = document.querySelector(`dialog-component#${drawerId}`);
      if (!(drawer instanceof HTMLElement) || typeof drawer.showDialog !== 'function') {
        window.sessionStorage.removeItem(KEEP_FILTER_DRAWER_OPEN_STATE_KEY);
        return;
      }

      const dialog = drawer.querySelector('dialog');
      dialog?.classList.remove('filter-drawer--opened');
      drawer.showDialog();
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          dialog?.classList.add('filter-drawer--opened');
        });
      });

      if (keepSortingOpen) {
        const sortingDetails = drawer.querySelector('sorting-filter-component details');
        if (sortingDetails instanceof HTMLDetailsElement) {
          sortingDetails.open = true;
        }
      }

      window.sessionStorage.removeItem(KEEP_FILTER_DRAWER_OPEN_STATE_KEY);
    });
  }

  closeFilterDrawer() {
    const filterDrawer = document.querySelector(CollectionFilterDrawerComponent.DRAWER_SELECTOR);
    if (filterDrawer && typeof filterDrawer.closeDialog === 'function') {
      filterDrawer.closeDialog();
    }
  }

  initializeDrawerAccordions() {
    const filterDrawer = document.querySelector(CollectionFilterDrawerComponent.DRAWER_SELECTOR);
    if (!filterDrawer) return;

    const accordionItems = filterDrawer.querySelectorAll('accordion-custom.facets__item');
    accordionItems.forEach((accordionItem) => {
      if (accordionItem.dataset.pdpAccordionBound === 'true') return;

      const details = accordionItem.querySelector('details.facets__panel');
      const summary = details?.querySelector('summary.facets__summary');
      const panel = details?.querySelector('.details-content');

      if (!details || !summary || !panel) return;

      // Start with all filter panels collapsed, matching PDP closed-first behavior.
      details.open = false;
      panel.style.display = 'none';

      summary.addEventListener('click', (event) => {
        event.preventDefault();

        const isOpen = details.open;

        accordionItems.forEach((item) => {
          if (item === accordionItem) return;

          const otherDetails = item.querySelector('details.facets__panel');
          const otherPanel = otherDetails?.querySelector('.details-content');
          if (!otherDetails || !otherPanel || !otherDetails.open) return;

          this.slideUp(otherPanel, CollectionFilterDrawerComponent.ANIMATION_DURATION, () => {
            otherDetails.open = false;
          });
        });

        if (isOpen) {
          this.slideUp(panel, CollectionFilterDrawerComponent.ANIMATION_DURATION, () => {
            details.open = false;
          });
        } else {
          details.open = true;
          this.slideDown(panel, CollectionFilterDrawerComponent.ANIMATION_DURATION);

          // Match PDP behavior: smooth-scroll the opened question into position.
          window.setTimeout(() => {
            const scrollContainer = summary.closest('dialog') || filterDrawer;
            const offset = 100;

            if (scrollContainer instanceof HTMLElement) {
              const containerRect = scrollContainer.getBoundingClientRect();
              const summaryRect = summary.getBoundingClientRect();
              const targetTop =
                scrollContainer.scrollTop + (summaryRect.top - containerRect.top) - offset;

              scrollContainer.scrollTo({
                top: Math.max(0, targetTop),
                behavior: 'smooth',
              });
            }
          }, CollectionFilterDrawerComponent.ANIMATION_DURATION);
        }
      });

      accordionItem.dataset.pdpAccordionBound = 'true';
    });
  }

  slideUp(element, duration = 300, onComplete) {
    element.style.height = `${element.scrollHeight}px`;
    element.offsetHeight;

    element.style.transition = `height ${duration}ms ease`;
    element.style.height = '0px';
    element.style.overflow = 'hidden';

    window.setTimeout(() => {
      element.style.display = 'none';
      element.style.removeProperty('height');
      element.style.removeProperty('overflow');
      element.style.removeProperty('transition');
      if (typeof onComplete === 'function') onComplete();
    }, duration);
  }

  slideDown(element, duration = 300, onComplete) {
    element.style.display = 'block';
    const height = element.scrollHeight;

    element.style.height = '0px';
    element.style.overflow = 'hidden';
    element.offsetHeight;

    element.style.transition = `height ${duration}ms ease`;
    element.style.height = `${height}px`;

    window.setTimeout(() => {
      element.style.removeProperty('height');
      element.style.removeProperty('overflow');
      element.style.removeProperty('transition');
      if (typeof onComplete === 'function') onComplete();
    }, duration);
  }
}

customElements.define('collection-filter-drawer', CollectionFilterDrawerComponent);
